// Helper to safely render customer name
function renderCustomerName(customer) {
  if (!customer) return '-';
  if (typeof customer === 'string') return customer;
  if (typeof customer === 'object' && customer !== null) {
    if (Array.isArray(customer)) return '-';
    if (typeof customer.name === 'string') return customer.name;
    return '-';
  }
  if (typeof customer === 'number') return String(customer);
  return String(customer);
}
import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, Chip, Box
} from '@mui/material';
import { Pagination } from '@mui/material';
import { fetchCurrencySetting } from '../utils/fetchCurrencySetting';

const SaleList = ({ sales, loading }) => {
  const [currency, setCurrency] = useState('USD');

  // Fetch currency setting
  useEffect(() => {
    fetchCurrencySetting().then(setCurrency);
  }, []);

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return amount.toLocaleString(undefined, { style: 'currency', currency });
  };

  // Filter for today's sales
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const todaysSales = sales
    .filter(sale => {
      const saleDate = new Date(sale.createdAt);
      return saleDate >= today && saleDate < tomorrow;
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // Pagination
  const [page, setPage] = React.useState(1);
  const pageSize = 10;
  const totalPages = Math.ceil(todaysSales.length / pageSize) || 1;
  const pagedSales = todaysSales.slice((page - 1) * pageSize, page * pageSize);

  React.useEffect(() => { setPage(1); }, [sales]);

  return (
    <Paper sx={{ p: 3, mt: 4 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Sales History (Today)</Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Cashier</TableCell>
              <TableCell>Products</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Paid</TableCell>
              <TableCell>Change</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8}>Loading...</TableCell></TableRow>
            ) : pagedSales.length === 0 ? (
              <TableRow><TableCell colSpan={8}>No sales found for today.</TableCell></TableRow>
            ) : (
              pagedSales.map((sale) => {
                // Check if transaction has discounts
                const hasGlobalDiscount = sale.discount > 0;
                const hasItemDiscounts = sale.products.some(item => {
                  const originalPrice = item.product?.price;
                  return originalPrice && item.price < originalPrice;
                });
                const hasAnyDiscount = hasGlobalDiscount || hasItemDiscounts;

                return (
                <TableRow 
                  key={sale._id}
                  sx={{ 
                    backgroundColor: hasAnyDiscount ? '#f0f9ff' : undefined,
                    borderLeft: hasAnyDiscount ? '4px solid #0ea5e9' : undefined
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                      {sale.invoiceNumber || `#${sale._id?.slice(-8).toUpperCase()}`}
                    </Typography>
                  </TableCell>
                  <TableCell>{new Date(sale.createdAt).toLocaleString()}</TableCell>
                  <TableCell>{renderCustomerName(sale.customer)}</TableCell>
                  <TableCell>{sale.user?.username || '-'}</TableCell>
                  <TableCell>
                    <Box>
                      {sale.products.map((item, idx) => {
                        const originalPrice = item.product?.price;
                        const costPrice = item.product?.costPrice || 0;
                        const isDiscounted = originalPrice && item.price < originalPrice;
                        const isBelowCost = costPrice > 0 && item.price < costPrice;
                        const discountPercent = isDiscounted 
                          ? Math.round(((originalPrice - item.price) / originalPrice) * 100)
                          : 0;
                        const lossAmount = isBelowCost ? (costPrice - item.price) : 0;

                        return (
                          <Box 
                            key={idx} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              mb: idx < sale.products.length - 1 ? 0.5 : 0,
                              py: 0.5,
                              px: (isDiscounted || isBelowCost) ? 1 : 0,
                              bgcolor: isBelowCost ? '#fee2e2' : isDiscounted ? '#dcfce7' : 'transparent',
                              borderRadius: 1,
                              border: isBelowCost ? '1px solid #fca5a5' : isDiscounted ? '1px solid #86efac' : 'none'
                            }}
                          >
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {item.product?.name || '-'} x {item.quantity} @ {formatCurrency(item.price)}
                            </Typography>
                            {isBelowCost && (
                              <Chip 
                                label={`LOSS ${formatCurrency(lossAmount)}`}
                                size="small"
                                sx={{ 
                                  bgcolor: '#dc2626',
                                  color: 'white',
                                  height: 20, 
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            )}
                            {isDiscounted && !isBelowCost && (
                              <Chip 
                                label={`-${discountPercent}%`}
                                size="small"
                                color="success"
                                sx={{ 
                                  height: 20, 
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            )}
                            {isBelowCost && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  textDecoration: 'line-through',
                                  color: 'text.secondary',
                                  fontSize: '0.7rem'
                                }}
                              >
                                Cost: {formatCurrency(costPrice)}
                              </Typography>
                            )}
                            {isDiscounted && !isBelowCost && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  textDecoration: 'line-through',
                                  color: 'text.secondary',
                                  fontSize: '0.7rem'
                                }}
                              >
                                {formatCurrency(originalPrice)}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                      {hasGlobalDiscount && (
                        <Chip 
                          label={`Global Discount: ${formatCurrency(sale.discount)}`}
                          size="small"
                          color="warning"
                          sx={{ 
                            mt: 0.5,
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={600}>{formatCurrency(sale.total)}</Typography>
                      {hasAnyDiscount && (
                        <Chip 
                          label="DISCOUNTED"
                          size="small"
                          sx={{ 
                            bgcolor: '#0ea5e9',
                            color: 'white',
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{typeof sale.paidAmount === 'number' ? sale.paidAmount.toFixed(2) : '-'}</TableCell>
                  <TableCell>{typeof sale.change === 'number' ? sale.change.toFixed(2) : '-'}</TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="center" alignItems="center" mt={2}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
          shape="rounded"
          showFirstButton
          showLastButton
        />
      </Stack>
    </Paper>
  );
};

export default SaleList;
